/*
  The cart (item 17, 2026-10-01). One script for the drawer and the cart page; the markup is
  snippets/ps-cart.liquid, the styles assets/ps-cart.css.

  The theme's contract is kept: <cart-drawer> with open(), close(), renderContents(), getSectionsToRender()
  and setActiveElement() (product-form.js calls them on Add to Cart), the "active" class (ps-popup reads it)
  and the 'cart-drawer' and 'cart-icon-bubble' sections.

  Motion (the premium feel brief): the drawer eases in and out, the rows settle in on open, a changed number
  fades up into place, a removed row folds away, an added row arrives softly. All of it is transform and
  opacity only, and prefers-reduced-motion turns it into plain fades or nothing (the CSS does that).
*/
(() => {
  if (customElements.get('cart-drawer')) return;

  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const wait = (ms) => new Promise((r) => setTimeout(r, RM.matches ? 0 : ms));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const jsonPost = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json().then((j) => ({ ok: r.ok, j })));

  const announce = () => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ps-cart' });
  };

  const PsCart = {
    root(el) {
      return el.closest('.ps-cart');
    },
    sections(root) {
      return root.dataset.ctx === 'page' ? [root.dataset.section, 'cart-icon-bubble'] : ['cart-drawer', 'cart-icon-bubble'];
    },

    /* Swap the cart body for the server's render, keeping scroll and focus, and animate what changed. */
    swap(root, sections, opts = {}) {
      const key = root.dataset.ctx === 'page' ? root.dataset.section : 'cart-drawer';
      const html = sections && sections[key];
      if (html) {
        const fresh = parse(html).querySelector('.ps-cart');
        if (fresh) {
          const nums = {};
          root.querySelectorAll('[data-pc-num]').forEach((n) => (nums[n.dataset.pcNum] = n.textContent.trim()));
          const scroller = root.querySelector('.pc-scroll');
          const top = scroller ? scroller.scrollTop : 0;
          const active = document.activeElement;
          const focusKey = active && root.contains(active) ? this.focusKey(active) : null;
          const noteOpen = !!root.querySelector('.pc-note[open]');

          const wasEmpty = root.classList.contains('is-empty');
          root.className = fresh.className;
          root.dataset.count = fresh.dataset.count;
          root.innerHTML = fresh.innerHTML;

          if (!wasEmpty && root.classList.contains('is-empty')) {
            root.classList.add('pc-enter');
            setTimeout(() => root.classList.remove('pc-enter'), 900);
          }
          if (noteOpen) {
            const n = root.querySelector('.pc-note');
            if (n) n.open = true;
          }
          const sc = root.querySelector('.pc-scroll');
          if (sc) sc.scrollTop = top;
          root.querySelectorAll('[data-pc-num]').forEach((n) => {
            const was = nums[n.dataset.pcNum];
            if (was !== undefined && was !== n.textContent.trim()) this.tick(n);
          });
          if (opts.added) {
            const row = root.querySelector(`.pc-line[data-variant="${opts.added}"]`);
            if (row) {
              row.classList.add('pc-new');
              row.addEventListener('animationend', () => row.classList.remove('pc-new'), { once: true });
            }
          }
          if (focusKey) this.refocus(root, focusKey);
          this.bar(root);
        }
      }
      this.bubble(sections);
      if (root.dataset.ctx === 'drawer') {
        const drawer = root.closest('cart-drawer');
        if (drawer) drawer.classList.toggle('is-empty', root.classList.contains('is-empty'));
      }
    },

    bubble(sections) {
      const html = sections && sections['cart-icon-bubble'];
      const el = document.getElementById('cart-icon-bubble');
      if (!html || !el) return;
      const src = parse(html).querySelector('.shopify-section');
      if (!src) return;
      const before = el.querySelector('.cart-count-bubble span');
      const was = before ? before.textContent.trim() : '';
      el.innerHTML = src.innerHTML;
      const after = el.querySelector('.cart-count-bubble span');
      if (after && after.textContent.trim() !== was) this.tick(after);
    },

    tick(el) {
      el.classList.remove('pc-tick');
      void el.offsetWidth;
      el.classList.add('pc-tick');
      el.addEventListener('animationend', () => el.classList.remove('pc-tick'), { once: true });
    },

    focusKey(el) {
      const line = el.closest('.pc-line');
      if (line && el.matches('[data-pc-qty]')) {
        const i = [...line.querySelectorAll('[data-pc-qty]')].indexOf(el);
        return { line: line.dataset.key, qty: i };
      }
      if (el.matches('[data-pc-pair]')) return { sel: '.pc-go' };
      return null;
    },
    refocus(root, k) {
      let t = null;
      if (k.line) {
        const line = root.querySelector(`.pc-line[data-key="${CSS.escape(k.line)}"]`);
        if (line) t = line.querySelectorAll('[data-pc-qty]')[k.qty];
      } else if (k.sel) t = root.querySelector(k.sel);
      if (t) t.focus({ preventScroll: true });
    },

    say(root, msg) {
      const live = root.querySelector('[data-pc-live]');
      if (live) live.textContent = msg;
    },

    async change(root, line, quantity) {
      const row = root.querySelector(`.pc-line[data-key="${CSS.escape(line)}"]`);
      const removing = quantity === 0;
      root.classList.add('is-busy');
      if (row) row.classList.add('is-busy');
      const req = jsonPost(window.routes.cart_change_url + '.js', {
        id: line,
        quantity,
        sections: this.sections(root),
        sections_url: window.location.pathname,
      });
      let fold = Promise.resolve();
      const gone = removing && row ? {
        id: row.dataset.variant,
        qty: parseInt((row.querySelector('.pc-qn') || {}).textContent, 10) || 1,
        name: (row.querySelector('.pc-name') || {}).textContent || 'Item',
      } : null;
      if (removing && row) fold = this.fold(row);
      try {
        const [{ ok, j }] = await Promise.all([req, fold]);
        if (!ok || j.status) {
          if (row) {
            row.classList.remove('is-busy', 'is-going');
            row.style.height = '';
            const err = row.querySelector('.pc-err');
            if (err) {
              err.textContent = j.description || j.message || window.cartStrings.error;
              err.hidden = false;
            }
          }
          return;
        }
        this.swap(root, j.sections);
        if (gone) this.undo(root, gone);
        this.say(root, removing ? gone.name.trim() + ' removed.' : 'Cart updated.');
        announce();
      } catch (e) {
        if (row) row.classList.remove('is-busy', 'is-going');
        this.say(root, window.cartStrings.error);
      } finally {
        root.classList.remove('is-busy');
      }
    },

    /* A removed row: fades, then folds its height away, so the rows below close up rather than jump. */
    fold(row) {
      if (RM.matches) return Promise.resolve();
      const h = row.getBoundingClientRect().height;
      row.style.height = h + 'px';
      row.classList.add('is-going');
      void row.offsetWidth;
      row.style.height = '0px';
      return wait(340);
    },

    /* After a remove, one quiet line with Undo for six seconds, then it fades away. */
    undo(root, gone) {
      const host = root.querySelector('.pc-scroll') || root.querySelector('.pc-empty');
      if (!host) return;
      const el = document.createElement('div');
      el.className = 'pc-undo';
      const t = document.createElement('span');
      t.textContent = gone.name.trim() + ' removed.';
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = 'Undo';
      el.append(t, b);
      const anchor = host.classList.contains('pc-empty') ? host.querySelector('.pc-et') : null;
      if (anchor) anchor.after(el);
      else host.prepend(el);
      const timer = setTimeout(() => {
        el.classList.add('is-out');
        setTimeout(() => el.remove(), 220);
      }, 6000);
      b.addEventListener('click', async () => {
        clearTimeout(timer);
        b.disabled = true;
        const { ok, j } = await jsonPost(window.routes.cart_add_url + '.js', {
          items: [{ id: Number(gone.id), quantity: gone.qty }],
          sections: this.sections(root),
          sections_url: window.location.pathname,
        }).catch(() => ({ ok: false, j: {} }));
        if (!ok || j.status) {
          b.disabled = false;
          this.say(root, j.description || window.cartStrings.error);
          return;
        }
        this.swap(root, j.sections, { added: gone.id });
        this.say(root, gone.name.trim() + ' is back in your cart.');
        announce();
      });
    },

    async add(root, btn) {
      const sel = root.querySelector('[data-pc-pair-variant]');
      const id = sel ? sel.value : btn.dataset.pcPair;
      btn.classList.add('is-busy');
      btn.setAttribute('aria-busy', 'true');
      try {
        const { ok, j } = await jsonPost(window.routes.cart_add_url + '.js', {
          items: [{ id: Number(id), quantity: 1 }],
          sections: this.sections(root),
          sections_url: window.location.pathname,
        });
        if (!ok || j.status) {
          btn.classList.remove('is-busy');
          btn.removeAttribute('aria-busy');
          this.say(root, j.description || window.cartStrings.error);
          return;
        }
        this.swap(root, j.sections, { added: id });
        this.say(root, 'Added to your cart.');
        announce();
      } catch (e) {
        btn.classList.remove('is-busy');
        btn.removeAttribute('aria-busy');
      }
    },

    /* The note saves in the background. Checkout never waits on it: the checkout form posts the note itself, so a
       pending save is cancelled on submit rather than raced against it (Shopify handles one cart change at a time). */
    note: null,
    noteCtl: null,
    saveNote(value, now) {
      clearTimeout(this.note);
      const send = () => {
        if (this.noteCtl) this.noteCtl.abort();
        this.noteCtl = new AbortController();
        fetch(window.routes.cart_update_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
          signal: this.noteCtl.signal,
        }).catch(() => {});
      };
      if (now) send();
      else this.note = setTimeout(send, 400);
    },
    cancelNote() {
      clearTimeout(this.note);
      if (this.noteCtl) this.noteCtl.abort();
    },

    /* The cart page on a phone: Subtotal and Checkout pinned until the summary's own Checkout is on screen. */
    io: null,
    bar(root) {
      if (root.dataset.ctx !== 'page' || !('IntersectionObserver' in window)) return;
      const bar = root.querySelector('.pc-bar');
      const go = root.querySelector('.pc-foot .pc-go');
      if (this.io) this.io.disconnect();
      if (!bar || !go) {
        document.documentElement.classList.remove('pc-bar-on');
        return;
      }
      this.io = new IntersectionObserver(([e]) => {
        const on = !e.isIntersecting && e.boundingClientRect.top > 0;
        bar.classList.toggle('is-on', on);
        document.documentElement.classList.toggle('pc-bar-on', on);
      });
      this.io.observe(go);
    },
  };
  window.PsCart = PsCart;

  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-pc-qty], [data-pc-remove], [data-pc-pair]');
    if (!t) return;
    const root = PsCart.root(t);
    if (!root || root.classList.contains('is-busy')) return;
    e.preventDefault();
    if (t.matches('[data-pc-pair]')) return PsCart.add(root, t);
    const line = t.closest('.pc-line');
    if (!line) return;
    const q = t.matches('[data-pc-remove]') ? 0 : Math.max(0, parseInt(t.dataset.pcQty, 10));
    PsCart.change(root, line.dataset.key, q);
  });
  document.addEventListener('input', (e) => {
    if (e.target.matches('[data-pc-note]')) PsCart.saveNote(e.target.value);
  });
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-pc-note]')) PsCart.saveNote(e.target.value, true);
  });
  /* Checkout leaves at once: nothing here waits or can cancel the submit. The pending note save is dropped (the form
     carries the note), and the button only changes how it looks while the checkout loads. */
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!f.matches('.pc-body')) return;
    PsCart.cancelNote();
    const go = e.submitter && e.submitter.matches('.pc-go') ? e.submitter : f.querySelector('.pc-go');
    if (go) {
      go.classList.add('is-going');
      go.setAttribute('aria-busy', 'true');
    }
  });
  window.addEventListener('pageshow', () =>
    document.querySelectorAll('.pc-go.is-going').forEach((b) => {
      b.classList.remove('is-going');
      b.removeAttribute('aria-busy');
    })
  );
  document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector('.ps-cart--page');
    if (page) PsCart.bar(page);
  });

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.header = document.querySelector('.header-wrapper');
      if (this.header) this.header.preventHide = false;
      this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.addEventListener('click', (evt) => {
        if (evt.target.closest('[data-pc-close]')) this.close();
      });
      this.setHeaderCartIconAccessibility();
    }

    get body() {
      return this.querySelector('.ps-cart');
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (this.header) this.header.preventHide = true;
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const wasOpen = this.classList.contains('active');
      if (!wasOpen && this.body) {
        /* The rows settle in once, as the panel arrives; not on every update while it is open. */
        this.body.classList.add('pc-enter');
        setTimeout(() => this.body && this.body.classList.remove('pc-enter'), 900);
      }
      void this.offsetWidth; /* commit the closed state first, so the panel transitions in */
      this.classList.add('active');
      document.documentElement.classList.add('pc-open');
      document.body.classList.add('overflow-hidden-drawer');
      const panel = this.querySelector('.pc-panel');
      const trap = () => {
        if (typeof trapFocus === 'function') trapFocus(this.querySelector('#CartDrawer'), panel);
      };
      if (wasOpen || RM.matches) trap();
      else panel.addEventListener('transitionend', trap, { once: true });
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active');
      document.documentElement.classList.remove('pc-open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden-drawer');
      if (this.header) this.header.preventHide = false;
    }

    /* Called by product-form.js after Add to Cart, with the section renders from /cart/add. */
    renderContents(parsedState) {
      this.productId = parsedState.id;
      const added = parsedState.variant_id || parsedState.id;
      if (this.body) PsCart.swap(this.body, parsedState.sections, { added: this.classList.contains('active') ? added : null });
      this.open();
    }

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
